import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCategory,
  deleteCategory,
  getAdminCategories,
  patchCategoryStatus,
  updateCategory,
} from '../services/adminApi';
import DeleteConfirmModal from './DeleteConfirmModal';

const EMPTY_FORM = { categoryName: '', displayOrder: '', status: true };

function useDebounce(value, delay) {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDeb(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return deb;
}

function validate(form) {
  const e = {};
  if (!form.categoryName.trim()) e.categoryName = 'Category name is required';
  else if (form.categoryName.trim().length < 2) e.categoryName = 'Name must be at least 2 characters';
  else if (form.categoryName.trim().length > 100) e.categoryName = 'Category name must be 100 characters or fewer';
  if (form.displayOrder !== '' && (isNaN(Number(form.displayOrder)) || Number(form.displayOrder) < 0))
    e.displayOrder = 'Must be a non-negative number';
  return e;
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" className={`admin-toggle ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}>
      <span className="admin-toggle__track"><span className="admin-toggle__thumb" /></span>
    </button>
  );
}

function SearchSelect({ value, onChange, options, placeholder }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) && !(dropRef.current && dropRef.current.contains(e.target))) {
        setOpen(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleOpen = () => setOpen((v) => !v);

  const selected = options.find((o) => o.value === value);
  const filtered = options.filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="admin-search-select" ref={wrapRef}>
      <button type="button" className="admin-search-select__trigger" onClick={handleOpen}>
        <span>{selected ? selected.label : (placeholder || 'Select')}</span>
        <svg viewBox="0 0 24 24" width="12" height="12"><path fill="currentColor" d="M7 10l5 5 5-5H7z" /></svg>
      </button>
      {open && (
        <div className="admin-search-select__dropdown" ref={dropRef}>
          <div className="admin-search-select__search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus onClick={(e) => e.stopPropagation()} />
          </div>
          <ul>
            {filtered.length === 0 && <li className="admin-search-select__empty">No options found</li>}
            {filtered.map((o) => (
              <li key={o.value} className={o.value === value ? 'is-selected' : ''} onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); setSearch(''); }}>
                {o.value === value && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M20 6L9 17l-5-5" /></svg>}
                {o.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CategoriesPage() {
  const [cats, setCats]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]     = useState(true);
  const [keyword, setKeyword]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageFlash, setPageFlash] = useState(null);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formErr, setFormErr]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling]   = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const debKeyword = useDebounce(keyword, 350);

  const flash = (type, msg) => {
    setPageFlash({ type, msg });
    setTimeout(() => setPageFlash(null), 4000);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: pageSize };
    if (debKeyword) params.keyword = debKeyword;
    if (statusFilter !== '') params.status = statusFilter;
    getAdminCategories(params)
      .then((res) => {
        setCats(res.categories);
        setTotal(res.total);
        setPageSize(res.pageSize || 10);
        setTotalPages(res.totalPages || 1);
      })
      .catch((err) => flash('error', err.message))
      .finally(() => setLoading(false));
  }, [page, pageSize, debKeyword, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(1); }, [debKeyword, statusFilter]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErr({});
    setModal('create');
  };

  const openEdit = (cat) => {
    setForm({
      categoryName: cat.categoryName || '',
      displayOrder: cat.displayOrder != null ? String(cat.displayOrder) : '',
      status: cat.status !== false,
    });
    setFormErr({});
    setModal(cat);
  };

  const closeModal = () => { setModal(null); setFormErr({}); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setFormErr(errs); return; }
    setSubmitting(true);
    setFormErr({});
    const body = {
      categoryName: form.categoryName.trim(),
      ...(form.displayOrder !== '' ? { displayOrder: Number(form.displayOrder) } : {}),
      status: form.status,
    };
    try {
      if (modal === 'create') {
        await createCategory(body);
        flash('success', 'Category created successfully.');
      } else {
        await updateCategory(modal.id, body);
        flash('success', 'Category updated successfully.');
      }
      closeModal();
      load();
    } catch (err) {
      setFormErr({ _api: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (cat) => {
    if (toggling) return;
    const newStatus = !(cat.status === true || cat.status === 'active');
    setToggling(cat.id);
    try {
      await patchCategoryStatus(cat.id, newStatus);
      flash('success', `Category ${newStatus ? 'activated' : 'deactivated'} successfully.`);
      load();
    } catch (err) {
      flash('error', err.message);
    } finally {
      setToggling(null);
    }
  };

  const hasActiveQuestions = (cat) => Number(cat.activeQuestionCount || 0) > 0;

  const handleDelete = async (cat) => {
    if (hasActiveQuestions(cat) || deleting) return;
    setDeleting(cat.id);
    try {
      await deleteCategory(cat.id);
      flash('success', 'Category deleted successfully.');
      if (cats.length === 1 && page > 1) setPage((current) => current - 1);
      else load();
    } catch (err) {
      flash('error', err.message);
    } finally {
      setDeleting(null);
      setDeleteTarget(null);
    }
  };

  const isActive = (cat) => cat.status === true || cat.status === 'active';

  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && arr[idx - 1] !== p - 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Categories</h1>
          <p className="admin-page__sub">Manage chatbot question categories</p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          New Category
        </button>
      </div>

      <div className="admin-panel">
        {pageFlash && (
          <div className={`admin-page-alert admin-page-alert--${pageFlash.type}`}>
            {pageFlash.type === 'success'
              ? <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z" clipRule="evenodd"/></svg>
              : <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd"/></svg>
            }
            {pageFlash.msg}
          </div>
        )}

        <div className="admin-toolbar">
          <div className="admin-toolbar__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search categories…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <SearchSelect
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All Status"
            options={[
              { value: '', label: 'All Status' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
          />
        </div>

        {loading ? (
          <div className="admin-loading">
            <span className="admin-spinner admin-spinner--dark" />
            Loading categories…
          </div>
        ) : cats.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
                <path d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
              </svg>
            </div>
            <h3>No categories found</h3>
            <p>{keyword || statusFilter ? 'Try clearing your filters.' : 'Create your first category to get started.'}</p>
            {!keyword && !statusFilter && (
              <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>
                Create Category
              </button>
            )}
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--categories">
              <colgroup>
                <col className="admin-table--categories__index" />
                <col className="admin-table--categories__name" />
                <col className="admin-table--categories__order" />
                <col className="admin-table--categories__question-count" />
                <col className="admin-table--categories__status" />
                <col className="admin-table--categories__actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category Name</th>
                  <th>Display Order</th>
                  <th>Active Questions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cats.map((cat, i) => (
                  <tr key={cat.id}>
                    <td style={{ color: 'var(--a-muted)', fontWeight: 500 }}>
                      {(page - 1) * pageSize + i + 1}
                    </td>
                    <td className="admin-table__q">
                      <span className="admin-table__truncate" title={cat.categoryName}>
                        {cat.categoryName}
                      </span>
                    </td>
                    <td>
                      {cat.displayOrder != null ? (
                        <span className="admin-mono">{cat.displayOrder}</span>
                      ) : (
                        <span style={{ color: 'var(--a-muted)' }}>—</span>
                      )}
                    </td>
                    <td><span className="admin-mono">{cat.activeQuestionCount || 0}</span></td>
                    <td>
                      {toggling === cat.id
                        ? <span className="admin-spinner admin-spinner--dark" style={{ width: 16, height: 16 }} />
                        : <Toggle checked={isActive(cat)} onChange={() => handleToggle(cat)} />}
                    </td>
                    <td>
                      <div className="admin-table__actions">
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          onClick={() => openEdit(cat)}
                        >
                          Edit
                        </button>
                        {!hasActiveQuestions(cat) && (
                          <button
                            type="button"
                            className="admin-btn admin-btn--ghost admin-btn--sm admin-btn--icon-danger"
                            onClick={() => setDeleteTarget(cat)}
                            disabled={deleting === cat.id}
                            title="Delete category with no active questions"
                          >
                            {deleting === cat.id ? <span className="admin-spinner admin-spinner--dark" /> : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && cats.length > 0 && (
          <div className="admin-pagination">
            <div className="admin-pagination__info">
              Showing <strong>{(page - 1) * pageSize + 1}</strong>–<strong>{Math.min(page * pageSize, total)}</strong> of <strong>{total}</strong> categories
            </div>
            <div className="admin-pagination__btns">
              <button className="admin-pagination__btn" onClick={() => setPage(1)} disabled={page <= 1} aria-label="First page">«</button>
              <button className="admin-pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={page <= 1} aria-label="Previous page">‹</button>
              {pageNums.map((p, i) =>
                p === '…' ? (
                  <span key={`e${i}`} className="admin-pagination__ellipsis">…</span>
                ) : (
                  <button key={p} className={`admin-pagination__btn${page === p ? ' is-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                )
              )}
              <button className="admin-pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages} aria-label="Next page">›</button>
              <button className="admin-pagination__btn" onClick={() => setPage(totalPages)} disabled={page >= totalPages} aria-label="Last page">»</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="cat-modal-title">
          <div className="admin-modal__backdrop" onClick={closeModal} />
          <div className="admin-modal__box">
            <div className="admin-modal__head">
              <h2 id="cat-modal-title">
                {modal === 'create' ? 'New Category' : 'Edit Category'}
              </h2>
              <button type="button" className="admin-modal__close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="admin-modal__body">
                {formErr._api && (
                  <div className="admin-alert admin-alert--error">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd"/></svg>
                    {formErr._api}
                  </div>
                )}

                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="cat-name">
                    Category Name <span>*</span>
                  </label>
                  <input
                    id="cat-name"
                    className={`admin-form-input${formErr.categoryName ? ' is-error' : ''}`}
                    type="text"
                    maxLength={100}
                    placeholder="e.g. Market Intelligence"
                    value={form.categoryName}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, categoryName: e.target.value }));
                      if (formErr.categoryName) setFormErr((fe) => { const n = { ...fe }; delete n.categoryName; return n; });
                    }}
                    autoFocus
                  />
                  <div className="admin-form-hint">{form.categoryName.length}/100 characters</div>
                  {formErr.categoryName && <div className="admin-form-error">{formErr.categoryName}</div>}
                </div>

                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label className="admin-form-label" htmlFor="cat-order">Display Order</label>
                    <input
                      id="cat-order"
                      className={`admin-form-input${formErr.displayOrder ? ' is-error' : ''}`}
                      type="number"
                      min="0"
                      placeholder="e.g. 1"
                      value={form.displayOrder}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, displayOrder: e.target.value }));
                        if (formErr.displayOrder) setFormErr((fe) => { const n = { ...fe }; delete n.displayOrder; return n; });
                      }}
                    />
                    {formErr.displayOrder && <div className="admin-form-error">{formErr.displayOrder}</div>}
                    <div className="admin-form-hint">Lower number = shown first</div>
                  </div>

                  <div className="admin-form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: 28 }}>
                    <label className="admin-checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.status}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked }))}
                      />
                      <span>Active (visible to users)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="admin-modal__foot">
                <button type="button" className="admin-btn admin-btn--ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn--primary" disabled={submitting}>
                  {submitting && <span className="admin-spinner" />}
                  {submitting ? 'Saving…' : modal === 'create' ? 'Create Category' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          itemType="category"
          itemLabel={deleteTarget.categoryName}
          busy={deleting === deleteTarget.id}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget)}
        />
      )}
    </div>
  );
}
